import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useRole } from "../lib/roleContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { StatusBadge } from "../components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { ChevronLeft, Wrench, Ban, FileCheck2, Stethoscope, Sparkles } from "lucide-react";

const ACTIONS = [
  { id: "restrict_access", label: "Restrict access to area", Icon: Ban, role: "Maintenance Lead" },
  { id: "assign_repair", label: "Assign repair", Icon: Wrench, role: "Maintenance Lead" },
  { id: "assign_first_aider", label: "Assign first aider", Icon: Stethoscope, role: "Club Secretary" },
  { id: "close_with_evidence", label: "Close with evidence", Icon: FileCheck2, role: "Maintenance Lead" },
];

function fmt(iso) { return iso ? new Date(iso).toLocaleString() : "—"; }

export default function Incident() {
  const { id } = useParams();
  const nav = useNavigate();
  const { role } = useRole();
  const [inc, setInc] = useState(null);
  const [pending, setPending] = useState(null);
  const [notes, setNotes] = useState("");

  const refresh = async () => setInc(await api.getIncident(id));
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [id]);

  if (!inc) return <Card className="p-8 text-sm text-muted-foreground">Loading…</Card>;

  const c = inc.ai_classification || {};
  const sev = c.severity || "low";
  const sevStatus = sev === "high" ? "red" : sev === "medium" ? "amber" : "green";

  const submit = async () => {
    await api.recordDecision({
      target_type: "incident",
      target_id: inc.id,
      reviewer_role: role.label,
      reviewer_name: role.name,
      action: pending.id,
      notes,
    });
    toast.success(`${pending.label} recorded`);
    setPending(null); setNotes("");
    refresh();
  };

  const makeReceipt = async () => {
    const r = await api.createReceipt({ incident_id: inc.id });
    nav(`/receipts/${r.id}`);
  };

  return (
    <div className="space-y-8" data-testid="incident-page">
      <Link to="/incidents" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-incidents">
        <ChevronLeft className="h-4 w-4" /> Back to incidents
      </Link>

      <header className="space-y-2">
        <div className="text-[11px] tracking-eyebrow text-muted-foreground">Incident · {inc.id}</div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-medium leading-tight">{c.summary || inc.text.slice(0, 120)}</h1>
        <div className="text-xs text-muted-foreground font-mono-ui">
          Raised by {inc.raised_by} · {fmt(inc.raised_at)} {inc.location && <>· {inc.location}</>}
        </div>
      </header>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6 border-border space-y-5">
          <div>
            <div className="text-[11px] tracking-eyebrow text-muted-foreground">Plain-English report</div>
            <p className="mt-1.5 text-[15px] leading-relaxed">“{inc.text}”</p>
          </div>

          <div className="dashed-divider" />

          <div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-[11px] tracking-eyebrow text-muted-foreground">
                <Sparkles className="h-3.5 w-3.5" /> AI risk assistant
              </div>
              <span className="text-[10px] tracking-eyebrow font-mono-ui px-1.5 py-0.5 rounded border border-border bg-white">Advisory only</span>
            </div>
            <div className="grid sm:grid-cols-4 gap-3 mt-3 text-xs">
              <KV label="Category" value={c.category} />
              <KV label="Severity" node={<StatusBadge status={sevStatus} label={sev} />} />
              <KV label="Affected" value={(c.affected_groups || []).join(", ")} />
              <KV label="Escalate to" value={(c.escalation_route || []).join(" · ")} />
            </div>
            <div className="mt-4">
              <div className="text-[11px] tracking-eyebrow text-muted-foreground">Recommended actions</div>
              <ul className="mt-1.5 space-y-1.5 text-sm">
                {(c.recommended_actions || []).map((a, i) => (
                  <li key={i} className="flex gap-2"><span className="font-mono-ui text-muted-foreground">{(i + 1).toString().padStart(2, "0")}</span><span>{a}</span></li>
                ))}
              </ul>
            </div>
          </div>
        </Card>

        <Card className="p-6 border-border space-y-4">
          <div className="text-[11px] tracking-eyebrow text-muted-foreground">Status</div>
          <div className="text-lg font-medium capitalize">{inc.status}</div>
          <div className="text-xs text-muted-foreground">
            {inc.linked_exceptions?.length ? `Linked to ${inc.linked_exceptions.length} event exception(s).` : "No linked event exceptions."}
          </div>
          <div className="pt-2">
            <Button variant="outline" className="w-full" onClick={makeReceipt} data-testid="incident-receipt-btn">
              Generate evidence receipt
            </Button>
          </div>
        </Card>
      </div>

      <section className="space-y-3" data-testid="incident-governance">
        <div className="text-[11px] tracking-eyebrow text-muted-foreground">Governance gate · Acting as {role.label}</div>
        <Card className="p-6 border-border flex flex-wrap gap-2">
          {ACTIONS.map((a) => {
            const allowed = role.label === a.role || role.id === "club_secretary";
            return (
              <Button
                key={a.id} variant="outline"
                disabled={!allowed || inc.status === "closed"}
                onClick={() => { setPending(a); setNotes(""); }}
                data-testid={`incident-action-${a.id}`}
              >
                <a.Icon className="h-3.5 w-3.5 mr-1.5" /> {a.label}
              </Button>
            );
          })}
          {inc.status === "closed" && <span className="text-xs text-muted-foreground">Incident closed.</span>}
        </Card>
      </section>

      <section>
        <div className="text-[11px] tracking-eyebrow text-muted-foreground mb-2">Evidence trail</div>
        <Card className="p-6 border-border">
          {inc.decisions.length === 0 ? (
            <div className="text-sm text-muted-foreground">No human decisions recorded yet.</div>
          ) : (
            <ol className="space-y-3 font-mono-ui text-xs">
              {inc.decisions.map((d) => (
                <li key={d.id} className="border-l-2 border-border pl-3" data-testid={`incident-decision-${d.id}`}>
                  <div className="text-muted-foreground">{fmt(d.timestamp)} — {d.id}</div>
                  <div className="text-sm font-sans"><b>{d.reviewer_name}</b> ({d.reviewer_role}) → {d.action}</div>
                  {d.notes && <div className="text-muted-foreground font-sans mt-0.5">“{d.notes}”</div>}
                </li>
              ))}
            </ol>
          )}
        </Card>
      </section>

      <Dialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{pending?.label}</DialogTitle>
            <DialogDescription>Recorded as a decision by <b>{role.name}</b> ({role.label}).</DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Add a short note for the evidence trail (optional)…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid="incident-decision-notes"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPending(null)}>Cancel</Button>
            <Button onClick={submit} data-testid="incident-decision-confirm">Record decision</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KV({ label, value, node }) {
  return (
    <div>
      <div className="text-[11px] tracking-eyebrow text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm font-medium">{node || value || "—"}</div>
    </div>
  );
}
